import { Alert, Card, Divider, Metric, Text } from "@/design-system";
import {
  type DelinquencyResult as Result,
  formatCurrency,
  formatPercent,
} from "../utils/finance";

type DelinquencyResultProps = {
  result: Result;
  stores: number;
  benchmarkRecoveryRate: number;
};

export const DelinquencyResult = ({
  result,
  stores,
  benchmarkRecoveryRate,
}: DelinquencyResultProps) => (
  <Card className="flex flex-col gap-6">
    <div aria-live="polite">
      <Metric
        label="Sua loja deixa na rua por ano"
        value={formatCurrency(result.lostYearly)}
        hint={`${formatCurrency(result.lostMonthly)} por mês em parcelas que atrasam e não voltam`}
      />
    </div>
    <Divider />
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="flex flex-col gap-1">
        <Text as="dt" size="small" tone="subtle">
          Em atraso por mês
        </Text>
        <Text as="dd" weight="semibold" numeric>
          {formatCurrency(result.overdueMonthly)}
        </Text>
      </div>
      <div className="flex flex-col gap-1">
        <Text as="dt" size="small" tone="subtle">
          Recuperado hoje por mês
        </Text>
        <Text as="dd" weight="semibold" numeric>
          {formatCurrency(result.recoveredMonthly)}
        </Text>
      </div>
      {stores > 1 ? (
        <div className="flex flex-col gap-1">
          <Text as="dt" size="small" tone="subtle">
            Perda por loja, por ano
          </Text>
          <Text as="dd" weight="semibold" numeric>
            {formatCurrency(result.lostYearlyPerStore)}
          </Text>
        </div>
      ) : null}
    </dl>
    {result.extraYearlyAtBenchmark > 0 ? (
      <Alert
        tone="success"
        title={`Mais ${formatCurrency(result.extraYearlyAtBenchmark)} por ano de volta ao caixa`}
      >
        É o que sua loja recuperaria a mais se chegasse à taxa média de
        recuperação das carteiras cobradas pela Fidexa, de{" "}
        {formatPercent(benchmarkRecoveryRate)} do valor em atraso.
      </Alert>
    ) : (
      <Alert tone="info" title="Sua recuperação já está acima da média">
        Sua loja recupera mais que a taxa média das carteiras cobradas pela
        Fidexa ({formatPercent(benchmarkRecoveryRate)}). O próximo ganho está em
        cobrar antes do atraso, para a parcela nem chegar a vencer em aberto.
      </Alert>
    )}
  </Card>
);
